import React, { Fragment } from "react";
import { cn } from "@/lib/utils";
import { LazyMotion, domAnimation, m } from "framer-motion";

export interface TwoColumnsFortySixtyData {
  sectionName?: string;
  smallTitle: string;
  title: string;
  text: string;
  leftTitle: string;
  leftText: string;
  paragraphs: { item: { title: string; text: string } }[];
}

interface TwoColumnsFortySixtyProps {
  data: TwoColumnsFortySixtyData;
}

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.7, ease: "easeOut" } },
};

export default function TwoColumnsFortySixty({
  data,
}: TwoColumnsFortySixtyProps) {
  const sectionId = data.sectionName && data.sectionName.replace(/\s+/g, "-");

  return (
    <LazyMotion features={domAnimation}>
      <section
      id={sectionId?.replaceAll(/[^a-zA-Z0-9-_]/g, "-")}
      data-name={data.sectionName}
      className="w-full bg-accent-color-three"
    >
      <div
        className={cn(
          "flex flex-col py-6 md:py-12 desktop:py-32 gap-12 mx-auto"
        )}
        style={{ maxWidth: "85vw" }}
      >
        <m.div
          className="flex flex-col gap-2"
          variants={fadeIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <div
            className={cn(
              "font-sans text-special-h2 uppercase text-secondary tracking-widest"
            )}
          >
            {data.smallTitle}
          </div>
          <div
            className={cn(
              "font-serif text-h2-mobile md:text-h2-tablet desktop:text-h2-desktop text-accent-color-two-light"
            )}
          >
            {data.title}
          </div>
        </m.div>
        <div className="flex flex-col desktop:flex-row gap-8">
          <m.div
            className={cn("w-full desktop:w-2/5 flex flex-col gap-6")}
            variants={fadeIn}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <div className={cn("font-sans text-paragraph-large text-white")}>
              {data.text}
            </div>
            {data.leftTitle && (
              <div
                className={cn(
                  "font-sans text-lg md:text-xl font-semibold tracking-widest uppercase text-[#F3E6D8]"
                )}
              >
                {data.leftTitle}
              </div>
            )}
            {data.leftText && (
              <div className={cn("font-sans text-paragraph-large text-white")}>
                {data.leftText}
              </div>
            )}
          </m.div>
          <m.div
            className={cn("w-full desktop:w-3/5 flex flex-col gap-6")}
            variants={fadeIn}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            {data.paragraphs.map((item, idx) => (
              <Fragment key={idx}>
                <div
                  className={cn(
                    "font-sans text-lg md:text-xl font-semibold text-[#FAFAFA]"
                  )}
                >
                  {item.item.title}
                </div>
                <div
                  className={cn("font-sans text-paragraph-large text-white")}
                  dangerouslySetInnerHTML={{ __html: item.item.text }}
                />
              </Fragment>
            ))}
          </m.div>
        </div>
      </div>
    </section>
    </LazyMotion>
  );
}
